import React, { Fragment, useState } from 'react';
import { IncentiveIssues, dhis_states } from '../../api/getAbhaMetricsDashboard';
import { cautionIconFilled, infoIconNew, tickIcon, tickIcon2 } from '../common/Icons';
import { AnimatePresence } from 'framer-motion';
import ABHAStepsModal from './ABHAStepsModal';
import { all_states, dhis_steps, getNextIncentiveState } from '../abha/util';
import NeedHelp from './NeedHelp';
import moment from 'moment';
import { AntTooltip } from '../common/AntTooltip';
import { track } from '../../tracking';
import { Steps } from 'antd';
import IncentiveSteps from './IncentiveSteps';

const contents: { [key: string]: any } = {
    'dhis-registration': {
        title: 'Submit your DHIS registration form',
        desc:
            "Any claims after 5th Jan will be processed in the next month and you'll receive incentives end of Feb. If done, make as completed here.",
        cta: {
            primary: {
                text: 'Register now',
            },
            secondary: {
                text: 'Mark as completed',
            },
        },
    },
    'dhis-approval': {
        title: 'Check DHIS registration status. It takes around 1 week for DHIS approval.',
        desc:
            'Check for the approval confirmation E-mail or go to DHIS dashboard > Claim your incentive. Once approved, mark it as approved here.',
        cta: {
            primary: {
                text: 'Check email',
            },
            secondary: {
                text: 'Mark as approved',
            },
            outline: {
                text: 'Go to DHIS dashboard',
            },
        },
    },
};

const DHISCard: React.FC<{
    onStatusChange: (
        result: {
            status?: all_states;
            month?: string;
            amount?: number;
            issue?: IncentiveIssues;
            dhisstatus?: dhis_states | undefined;
            dhisissues?: IncentiveIssues | undefined;
        },
        skipMonth?: boolean,
        onSuccess?: () => void,
        onFailure?: () => void,
    ) => void;
    currMonthStatus?: {
        status?: all_states;
        month?: string | undefined;
        amount?: number | undefined;
        issue?: IncentiveIssues | undefined;
        dhisstatus?: dhis_states | undefined;
        dhisissues?: IncentiveIssues | undefined;
    };
    setDhisStatus: (val?: dhis_states) => void;
    dhisStatus?: dhis_states;
}> = ({ onStatusChange, currMonthStatus, setDhisStatus, dhisStatus }) => {
    const [dhisDialogVisible, setDhisDialogVisible] = useState(false);
    const [needHelpDialogVisible, setNeedHelpDialogVisible] = useState(false);

    const close = () => setDhisDialogVisible(false);

    const status = currMonthStatus?.dhisstatus;

    if (!status) {
        return null;
    }

    const primaryCta = () => {
        if (status === 'dhis-registration') {
            track('DHIS_REG_STEPS');
            setDhisDialogVisible(true);
        } else {
            track('DHIS_REG_OPEN_DASHBOARD');
            window.open(
                'https://mail.google.com/mail/u/0/#search/from%3A(%40noreply.abdm%40nha.gov.in)+in%3Aanywhere',
                '_blank',
            );
        }
    };

    const secondaryCta = (onSuccess?: () => void, onFailure?: () => void) => {
        const nextstatus = getNextIncentiveState(status);

        if (status === 'dhis-registration') {
            track('DHIS_REG_STATUS_UPDATE', {
                status: nextstatus ?? 'dhis-registration',
                prevStatus: status,
            });

            onStatusChange(
                {
                    dhisstatus: (nextstatus ?? 'dhis-registration') as dhis_states,
                    amount: currMonthStatus?.amount,
                    issue: currMonthStatus?.issue,
                    dhisissues: currMonthStatus?.dhisissues
                        ? {
                              ...currMonthStatus.dhisissues,
                              isResolved: true,
                          }
                        : undefined,
                    month: undefined,
                },
                true,
                onSuccess,
                onFailure,
            );

            setDhisStatus((nextstatus ?? 'dhis-registration') as any);
        } else {
            track('DHIS_REG_STATUS_UPDATE', {
                status: 'dhis-end',
                prevStatus: status,
            });

            onStatusChange(
                {
                    dhisstatus: 'dhis-end',
                    amount: currMonthStatus?.amount,
                    issue: currMonthStatus?.issue,
                    status: nextstatus,
                    dhisissues: currMonthStatus?.dhisissues
                        ? {
                              ...currMonthStatus.dhisissues,
                              isResolved: true,
                          }
                        : undefined,
                },
                undefined,
                onSuccess,
                onFailure,
            );

            setDhisStatus('dhis-end');
        }
    };

    const outlineBtnCta = () => window.open('https://facility.ndhm.gov.in/', '_blank');

    const today = moment();
    const todayDate = today.date();
    const isCycleTwo = todayDate >= 5;

    const hasError = currMonthStatus?.dhisissues?.id && !currMonthStatus?.dhisissues.isResolved;

    return status === 'dhis-registration' || status === 'dhis-approval' ? (
        <Fragment>
            <div className={`flex flex-col bg-white rounded-8`}>
                <div className="rounded-t-8 gap-16 px-16 py-8 w-full flex items-center justify-between bg-white border-b border-darwin-neutral_new-200">
                    <IncentiveSteps currMonthStatus={currMonthStatus} dhisStatus={dhisStatus} />
                    <div className="flex flex-shrink-0 ml-auto justify-end self-start items-center gap-8">
                        <div
                            onClick={() => setNeedHelpDialogVisible(true)}
                            className="flex gap-4 text-13 bg-white cursor-pointer px-8 py-4 font-700 text-darwin-accent-bright-blue-800 rounded-4 justify-center items-center hover:bg-darwin-accent-bright-blue-100"
                        >
                            Need help?
                        </div>
                        {hasError && (
                            <AntTooltip
                                placement="bottom"
                                content={currMonthStatus?.issue?.remark ?? 'Error'}
                            >
                                <div className="flex gap-4 select-none text-12 bg-red-primary px-8 py-4 font-700 text-white rounded-4 justify-center items-center">
                                    {currMonthStatus?.dhisissues?.id === 'crn_error'
                                        ? 'CRN Error'
                                        : currMonthStatus?.dhisissues?.id === 'pfms_error'
                                        ? 'PFMS Error'
                                        : 'Error'}
                                </div>
                            </AntTooltip>
                        )}
                        <AntTooltip
                            content={
                                <div className="flex flex-col gap-8">
                                    <div className="flex flex-col gap-4">
                                        <p className="text-14">Cycle 1:</p>
                                        <p className="text-12">
                                            Claim incentive between 1 to 5th of month to recieve
                                            before the end of each month
                                        </p>
                                    </div>
                                    <div className="flex flex-col gap-4">
                                        <p className="text-14">Cycle 2:</p>
                                        <p className="text-12">
                                            Incentives claimed after 5th of month will be recieved
                                            next month
                                        </p>
                                    </div>
                                </div>
                            }
                            placement="bottomRight"
                        >
                            <div
                                style={{ background: '#E8E8E8' }}
                                className="flex gap-4 select-none text-12 px-8 py-4 font-700 text-white rounded-4 justify-center items-center"
                            >
                                {infoIconNew('fill-darwin-neutral_new-1000 w-14 h-14')}
                                {isCycleTwo ? (
                                    <p className="text-13 text-darwin-neutral-1000 font-400">
                                        Claim cycle 2
                                    </p>
                                ) : (
                                    <p className="text-13 text-darwin-neutral-1000 font-400">
                                        Claim cycle 1:{' '}
                                        <span className="font-700">{5 - todayDate} days left</span>
                                    </p>
                                )}
                            </div>
                        </AntTooltip>
                    </div>
                </div>
                <div className="flex justify-between gap-12 p-16">
                    <div className="flex gap-8 flex-col">
                        <p className="dhis-title font-700 text-16">{contents[status].title}</p>
                        <p className="dhis-desc text-13">{contents[status].desc}</p>
                    </div>
                    <div className="flex gap-16 items-center flex-wrap">
                        <div className="flex items-center ml-auto gap-16">
                            {contents[status].cta?.outline?.text && (
                                <p
                                    onClick={outlineBtnCta}
                                    className="text-14 cursor-pointer font-700 text-darwin-accent-bright-blue-800 hover:opacity-80"
                                >
                                    {contents[status].cta?.outline?.text}
                                </p>
                            )}
                            <div
                                onClick={primaryCta}
                                className="flex text-14 cursor-pointer text-white px-8 py-4 font-700 bg-darwin-accent-bright-blue-800 rounded-4 justify-center items-center hover:opacity-80"
                            >
                                {contents[status].cta.primary.text}
                            </div>
                        </div>
                        <div
                            onClick={() => secondaryCta()}
                            className="flex ml-auto gap-4 text-14 bg-white cursor-pointer px-8 py-4 font-700 border border-darwin-accent-bright-blue-800 text-darwin-accent-bright-blue-800 rounded-4 justify-center items-center hover:bg-darwin-accent-bright-blue-100"
                        >
                            {contents[status].cta.secondary.text}{' '}
                            <span>{tickIcon('w-14 h-14 fill-darwin-accent-bright-blue-800')}</span>
                        </div>
                    </div>
                </div>
            </div>
            <AnimatePresence>
                {dhisDialogVisible && (
                    <ABHAStepsModal
                        close={close}
                        onMark={secondaryCta}
                        markText={contents[status].cta.secondary.text}
                    />
                )}
            </AnimatePresence>

            <AnimatePresence>
                {needHelpDialogVisible && (
                    <NeedHelp
                        close={() => setNeedHelpDialogVisible(false)}
                        onSubmit={onStatusChange}
                        currMonthStatus={currMonthStatus}
                        dhisStatus={dhisStatus}
                    />
                )}
            </AnimatePresence>
        </Fragment>
    ) : null;
};

export default DHISCard;
